import React, { useRef, useState } from 'react';
import { View, TextInput, TouchableOpacity, StyleSheet, Text } from 'react-native';
import { Send, X } from '../../assets/icons';
import { theme } from '../constants/theme';
import { hp, wp } from '../helpers/common';
import Loading from './Loading';

interface ReplyInputProps {
  onSendReply: (text: string) => Promise<void>;
  onCancel: () => void;
  replyingTo: {
    id: string;
    user: {
      name: string;
    };
  };
  loading?: boolean;
}

const ReplyInput: React.FC<ReplyInputProps> = ({
  onSendReply,
  onCancel,
  replyingTo,
  loading = false,
}) => {
  const [replyText, setReplyText] = useState('');
  const inputRef = useRef<TextInput>(null);

  const handleSend = async () => {
    if (replyText.trim()) {
      await onSendReply(replyText.trim());
      setReplyText('');
    }
  };

  const handleCancel = () => {
    setReplyText('');
    onCancel();
  };

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.replyingToText}>
          Replying to {replyingTo.user.name}
        </Text>
        <TouchableOpacity onPress={handleCancel} style={styles.cancelButton}>
          <X size={20} color={theme.colors.textLight} />
        </TouchableOpacity>
      </View>
      
      <View style={styles.inputContainer}>
        <TextInput
          ref={inputRef}
          style={styles.textInput}
          placeholder="Write a reply..."
          placeholderTextColor={theme.colors.textLight}
          value={replyText}
          onChangeText={setReplyText}
          multiline
          maxLength={500}
          autoFocus
        />
        
        <View style={styles.actions}>
          <Text style={styles.characterCount}>
            {replyText.length}/500
          </Text>
          
          {loading ? (
            <View style={styles.loadingContainer}>
              <Loading size="small" />
            </View>
          ) : (
            <TouchableOpacity
              onPress={handleSend}
              style={[
                styles.sendButton,
                !replyText.trim() && styles.sendButtonDisabled
              ]}
              disabled={!replyText.trim() || loading}
            >
              <Send 
                size={18} 
                color={replyText.trim() ? theme.colors.primary : theme.colors.textLight} 
              />
            </TouchableOpacity>
          )}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: 'white',
    borderTopWidth: 1,
    borderTopColor: theme.colors.gray,
    paddingHorizontal: wp(4),
    paddingVertical: hp(1),
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: hp(1),
  },
  replyingToText: {
    fontSize: hp(1.4),
    color: theme.colors.textLight,
    fontWeight: theme.fonts.medium,
  },
  cancelButton: {
    padding: 4,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 10,
  },
  textInput: {
    flex: 1,
    borderWidth: 1,
    borderColor: theme.colors.gray,
    borderRadius: theme.radius.lg,
    paddingHorizontal: 12,
    paddingVertical: 8,
    fontSize: hp(1.6),
    color: theme.colors.textDark,
    maxHeight: hp(10),
    minHeight: hp(4),
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  characterCount: {
    fontSize: hp(1.2),
    color: theme.colors.textLight,
  },
  sendButton: {
    backgroundColor: theme.colors.backgroundLight,
    borderRadius: theme.radius.lg,
    padding: 8,
    borderWidth: 1,
    borderColor: theme.colors.primary,
  },
  sendButtonDisabled: {
    borderColor: theme.colors.gray,
  },
  loadingContainer: {
    padding: 8,
  },
});

export default ReplyInput;
